import type { GenericFormType } from '@neo/blocks/forms/types'
import type { JsonSchema } from '@shared/types'
import type { McpAction, McpTool, McpToolsResponse } from 'v2/pages/settings/types'
import type { RActionDetails, RExternalOrgApp } from 'v2/types/api/settings'
import { ActionFieldType, type AiActionNodeData } from '../types'
import { extractFieldsFromSchema } from '../utils/ai-action-utils'

interface UseActionFieldTypeProps {
  form: GenericFormType<AiActionNodeData['config']>
  arrayIndex: number
}

export const useActionFieldType = ({ form, arrayIndex }: UseActionFieldTypeProps) => {
  // Get field type from action_field_types
  const getFieldType = (field: string, isOptional: boolean): ActionFieldType => {
    const actionFieldTypes = form.getFieldValue(`actions[${arrayIndex}].action_field_types`)

    if (isOptional && actionFieldTypes?.optional_fields?.[field]) {
      return actionFieldTypes.optional_fields[field] as ActionFieldType
    }

    if (!isOptional && actionFieldTypes?.required_fields?.[field]) {
      return actionFieldTypes.required_fields[field] as ActionFieldType
    }

    const defaultType = isOptional ? ActionFieldType.MANUAL : ActionFieldType.AUTO

    if (defaultType === ActionFieldType.AUTO) {
      const value = form.getFieldValue(`actions[${arrayIndex}].action_inputs.${field}`)
      const hasManualData =
        value !== null &&
        value !== undefined &&
        !(typeof value === 'string' && value.trim().length === 0)

      if (hasManualData) {
        return ActionFieldType.MANUAL
      }
    }

    // Fallback defaults
    return defaultType
  }
  // Set field type in action_field_types
  const setFieldType = (
    field: string,
    type: ActionFieldType,
    isOptional: boolean,
    preserveValue?: boolean,
  ) => {
    const actionFieldTypes = form.getFieldValue(`actions[${arrayIndex}].action_field_types`) || {
      required_fields: {},
      optional_fields: {},
    }

    if (type === ActionFieldType.AUTO) {
      // Set to null for auto mode (will be filled by AI)
      form.setFieldValue(`actions[${arrayIndex}].action_inputs.${field}`, null)

      // Clear any validation errors when switching to auto mode
      form.setFieldMeta(`actions[${arrayIndex}].action_inputs.${field}`, (prev) => ({
        ...prev,
        errors: [],
        isTouched: true,
      }))
    } else if (type === ActionFieldType.MANUAL) {
      // Only clear the value if we're not preserving it
      if (!preserveValue) {
        form.setFieldValue(`actions[${arrayIndex}].action_inputs.${field}`, '')
      }
      // Mark the field as touched
      form.setFieldMeta(`actions[${arrayIndex}].action_inputs.${field}`, (prev) => ({
        ...prev,
        isTouched: true,
      }))
    }

    // Update action_field_types
    if (isOptional) {
      actionFieldTypes.optional_fields = actionFieldTypes.optional_fields || {}
      actionFieldTypes.optional_fields[field] = type
    } else {
      actionFieldTypes.required_fields = actionFieldTypes.required_fields || {}
      actionFieldTypes.required_fields[field] = type
    }

    form.setFieldValue(`actions[${arrayIndex}].action_field_types`, actionFieldTypes)

    // Trigger validation for the specific field to update its validation state
    form.validateField(`actions[${arrayIndex}].action_inputs.${field}`, 'change')
  }

  return { getFieldType, setFieldType }
}

// Extract fields and schema from action
export const extractFieldsAndSchemaFromAction = (
  actionName: string | null,
  activeApps: RExternalOrgApp[] | undefined,
  mcpTools: McpToolsResponse | undefined,
  orgAppUuid?: string | null,
): { required: string[]; optional: string[]; schema?: McpAction['input_schema'] | JsonSchema } => {
  if (!actionName) return { required: [], optional: [] }

  // If orgAppUuid is provided, find the specific app first to avoid conflicts
  if (orgAppUuid && activeApps) {
    const specificApp = activeApps.find((app) => app.id === orgAppUuid)

    if (specificApp) {
      // Check regular app actions first
      const action = specificApp.actions?.find((a: RActionDetails) => a.action_name === actionName)
      if (action?.input_schema) {
        return extractFieldsFromSchema(action.input_schema)
      }

      // Check MCP tools for this specific app (keyed by orgAppUuid)
      if (mcpTools?.[orgAppUuid]) {
        const mcpTool = mcpTools[orgAppUuid]
        const mcpAction = mcpTool.actions?.find((a: McpAction) => a.name === actionName)
        if (mcpAction?.input_schema) {
          return extractFieldsFromSchema(mcpAction.input_schema)
        }
      }
    }
  }

  // Fallback: First try to find in regular apps (for backward compatibility)
  const appAction = (activeApps || []).find((app) =>
    app.actions?.find((a: RActionDetails) => a.action_name === actionName),
  )

  if (appAction) {
    const action = appAction.actions?.find((a: RActionDetails) => a.action_name === actionName)
    if (action?.input_schema) {
      return extractFieldsFromSchema(action.input_schema)
    }
  }

  // If not found in regular apps, try MCP tools
  if (mcpTools) {
    const mcpTool = Object.values(mcpTools).find((tool: McpTool) =>
      tool.actions?.find((a: McpAction) => a.name === actionName),
    )

    if (mcpTool) {
      const action = mcpTool.actions?.find((a: McpAction) => a.name === actionName)
      if (action?.input_schema) {
        return extractFieldsFromSchema(action.input_schema)
      }
    }
  }

  // Return empty arrays if no action found
  return { required: [], optional: [] }
}
